import { apiClient, apiClientPaginated } from '@/lib/api-client';
import type { CurrencyCode } from '@bbpos/types';
import { tenantService } from './tenant.service';

export interface PlatformStats {
  totals: {
    total: number;
    active: number;
    trial: number;
    pending_approval: number;
    suspended: number;
  };
  by_business_type: {
    RETAIL: number;
    RESTAURANT: number;
  };
  new_this_month: number;
}

// Money is per tenant currency, in that currency's minor unit (P-008): paise
// and baisa are never added together, so every amount is keyed by currency.
export interface PlatformTrendPoint {
  label: string;
  revenue: Partial<Record<CurrencyCode, number>>;
  previousRevenue: Partial<Record<CurrencyCode, number>>;
  tenants: number;
}

export interface PlatformRevenueTrend {
  range: '7d' | '30d' | '90d';
  /** Currencies with sales in the window, biggest current total first. */
  currencies: CurrencyCode[];
  points: PlatformTrendPoint[];
  total: Partial<Record<CurrencyCode, number>>;
  previousTotal: Partial<Record<CurrencyCode, number>>;
}

export interface PlatformCurrencyVolume {
  currency: CurrencyCode;
  revenue: number;
  transaction_count: number;
}

export interface PlatformSummary {
  today: {
    transaction_count: number;
    by_currency: Array<PlatformCurrencyVolume & { retail_share_percent: number }>;
  };
  yesterday: {
    transaction_count: number;
    by_currency: PlatformCurrencyVolume[];
  };
}

export interface PlatformAuditEvent {
  id: string;
  tenant_id?: string | null;
  user_id?: string | null;
  user_name?: string | null;
  action: string;
  entity_type: string;
  entity_id?: string | null;
  created_at: string;
  ip_address?: string | null;
}

export interface HealthResponse {
  status: 'ok' | 'error';
  uptime: number;
  checks: Record<string, 'ok' | 'error' | 'unavailable'>;
}

export const platformDashboardService = {
  getStats() {
    return apiClient<PlatformStats>('/tenants/platform-stats');
  },

  getRevenueTrend(range: '7d' | '30d' | '90d') {
    return apiClient<PlatformRevenueTrend>(`/dashboard/platform-revenue-trend?range=${range}`);
  },

  getSummary() {
    return apiClient<PlatformSummary>('/dashboard/platform-summary');
  },

  getRecentTenants() {
    return tenantService.list({ page: 1, limit: 10 });
  },

  getPendingApprovals() {
    return tenantService.list({ page: 1, limit: 10, status: 'PENDING_APPROVAL' });
  },

  getAuditEvents() {
    return apiClientPaginated<PlatformAuditEvent[]>('/audit?page=1&limit=10');
  },

  async getHealth(): Promise<HealthResponse> {
    // /health is excluded from the /api/v1 global prefix and bypasses the
    // ResponseInterceptor envelope, so we fetch it directly (not via apiClient).
    const res = await fetch('/health', { credentials: 'include' });
    const body = (await res.json()) as HealthResponse;
    return body;
  },
};
